import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Suspense, lazy, useState, type ComponentType, type ReactNode } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  BarChart3,
  ClipboardCheck,
  History,
  Languages,
  LayoutGrid,
  Loader2,
  Menu,
  RefreshCw,
  Type,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  backfillPlaceTranslations,
  getFinnishAutoTranslate,
  getPlaceTranslationCoverage,
  getTranslationBackfillRuns,
  setFinnishAutoTranslate,
} from "@/lib/translate-backfill.functions";
import { useAuth } from "@/hooks/use-auth";
import { BS } from "@/components/admin/_shared";
import { WorkspaceHeader, WorkspacePageTitle } from "@/components/WorkspaceHeader";

const FinnishReviewQueue = lazy(() =>
  import("@/components/admin/FinnishReviewQueue").then((m) => ({ default: m.FinnishReviewQueue })),
);
const FinnishContentBrowser = lazy(() =>
  import("@/components/admin/FinnishContentBrowser").then((m) => ({ default: m.FinnishContentBrowser })),
);
const SiteCopyTranslations = lazy(() =>
  import("@/components/admin/SiteCopyTranslations").then((m) => ({ default: m.SiteCopyTranslations })),
);

type TabKey = "overview" | "review" | "browse" | "copy" | "jobs";

const TAB_KEYS = new Set<string>(["overview", "review", "browse", "copy", "jobs"]);

const TABS: {
  key: TabKey;
  label: string;
  navLabel: string;
  description: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
}[] = [
  {
    key: "overview",
    label: "Coverage",
    navLabel: "Coverage",
    description: "How much of the catalogue has Finnish, and what’s still missing.",
    icon: BarChart3,
  },
  {
    key: "review",
    label: "Review queue",
    navLabel: "Review",
    description: "Approve, edit or regenerate AI Finnish drafts.",
    icon: ClipboardCheck,
  },
  {
    key: "browse",
    label: "Edit listings",
    navLabel: "Browse",
    description: "Open any listing and edit Finnish next to English.",
    icon: LayoutGrid,
  },
  {
    key: "copy",
    label: "Site copy",
    navLabel: "Copy",
    description: "Hero, greeting and homepage wording on /fi.",
    icon: Type,
  },
  {
    key: "jobs",
    label: "Background jobs",
    navLabel: "Jobs",
    description: "Scheduled and manual translation runs.",
    icon: History,
  },
];

export const Route = createFileRoute("/_authenticated/admin/translations")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === "string" && TAB_KEYS.has(search.tab) ? (search.tab as TabKey) : ("overview" as TabKey),
  }),
  head: () => ({
    meta: [
      { title: "Finnish translations — Admin" },
      { name: "description", content: "Review, edit and backfill Finnish translations." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminTranslationsPage,
});

function TabFallback() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl border bg-white animate-pulse" style={{ borderColor: "#eceff3" }} />
        ))}
      </div>
      <div className="h-72 rounded-xl border bg-white animate-pulse" style={{ borderColor: "#eceff3" }} />
    </div>
  );
}

function AdminTranslationsPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { isAdmin, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const coverageFn = useServerFn(getPlaceTranslationCoverage);
  const backfillFn = useServerFn(backfillPlaceTranslations);
  const runsFn = useServerFn(getTranslationBackfillRuns);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "translation-coverage"],
    queryFn: () => coverageFn({ data: undefined as never }),
    enabled: !!isAdmin,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const { data: runs, refetch: refetchRuns, isFetching: runsFetching } = useQuery({
    queryKey: ["admin", "translation-backfill-runs"],
    queryFn: () => runsFn({ data: undefined as never }),
    enabled: !!isAdmin && (tab === "jobs" || tab === "overview"),
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: tab === "jobs" ? 60_000 : false,
  });

  const active = TABS.find((t) => t.key === tab) ?? TABS[0];
  const pct = data && data.total > 0 ? Math.round((data.complete / data.total) * 100) : 0;
  const pending = data?.pending ?? 0;
  const lastRun = runs?.[0] ?? null;

  const go = (key: TabKey) => {
    setSidebarOpen(false);
    navigate({ to: "/admin/translations", search: { tab: key } });
  };

  async function runBackfill() {
    setRunning(true);
    setLog([]);
    try {
      for (let i = 0; i < 40; i++) {
        const res = await backfillFn({ data: { batch: 3 } });
        setLog((l) => [
          `Translated ${res.processed} listing${res.processed === 1 ? "" : "s"} · ${res.remaining} remaining`,
          ...res.errors,
          ...l,
        ]);
        if (res.errors.length > 0) toast.error(res.errors[0]!);
        if (res.remaining === 0 || res.processed === 0) break;
      }
      toast.success("Finnish backfill finished");
    } catch (err) {
      toast.error((err as Error).message || "Backfill failed");
    } finally {
      setRunning(false);
      void refetch();
      void refetchRuns();
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "#f4f6f8", fontFamily: "'Poppins', system-ui, sans-serif" }}>
        <div className="h-[53px] flex-shrink-0" style={{ background: BS.dark }} />
        <div className="flex-1 flex">
          <div className="hidden md:block w-[232px] border-r bg-[#f8f9fb]" style={{ borderColor: "#eceff3" }} />
          <div className="flex-1 p-7">
            <TabFallback />
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#f4f6f8" }}>
        <div className="bg-white border rounded-xl shadow-sm p-8 max-w-md text-center" style={{ borderColor: "#eceff3" }}>
          <h1 className="text-lg font-semibold text-[#212529]">Finnish translations</h1>
          <p className="mt-2 text-sm text-[#6c757d]">You don't have access to this workspace.</p>
          <Link to="/admin" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: BS.primary }}>
            <ArrowLeft className="size-4" /> Back to admin
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh overflow-hidden flex flex-col" style={{ background: "#f4f6f8", fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <WorkspaceHeader current="admin" onMenu={() => setSidebarOpen((v) => !v)} />

      <div className="flex-1 flex relative min-h-0">
        {sidebarOpen ? (
          <button
            type="button"
            aria-label="Close navigation"
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        ) : null}

        <aside
          className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static top-[53px] md:top-auto bottom-0 left-0 z-40 w-[232px] min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] border-r bg-[#f8f9fb] transition-transform duration-150 ease-out shadow-xl md:shadow-none`}
          style={{ borderColor: "#eceff3" }}
        >
          <div className="mb-1 flex items-center justify-between px-3 pt-3 md:hidden">
            <span className="text-sm font-semibold text-[#212529]">Menu</span>
            <button type="button" onClick={() => setSidebarOpen(false)} aria-label="Close" className="rounded-lg p-1.5 hover:bg-[#eef1f5]">
              <X className="size-4" />
            </button>
          </div>
          <div className="px-2.5 py-3 pb-10">
            <div className="px-2.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b95a1]">
              Finnish
            </div>
            <nav className="space-y-0.5">
              {TABS.map((t) => {
                const Icon = t.icon;
                const on = t.key === active.key;
                const badge = t.key === "overview" ? pending : 0;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => go(t.key)}
                    className="w-full flex items-center gap-2 px-1.5 py-[6px] rounded-lg text-[13px] font-medium text-left transition-colors duration-150"
                    style={{
                      background: on ? BS.primary : "transparent",
                      color: on ? "white" : "#1f2937",
                    }}
                    onMouseEnter={(e) => {
                      if (!on) e.currentTarget.style.background = "#eef1f5";
                    }}
                    onMouseLeave={(e) => {
                      if (!on) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span
                      className="size-6 rounded-md flex items-center justify-center shrink-0"
                      style={{
                        background: on ? "rgba(255,255,255,0.18)" : "#eceff3",
                        color: on ? "white" : "#4b5563",
                      }}
                    >
                      <Icon className="size-3.5" strokeWidth={1.85} />
                    </span>
                    <span className="flex-1 truncate leading-none">{t.navLabel}</span>
                    {badge > 0 && (
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
                        style={{
                          background: on ? "rgba(255,255,255,0.22)" : "#dc3545",
                          color: "white",
                        }}
                      >
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        <main className="min-w-0 flex-1 min-h-0 p-3 pb-24 sm:p-5 md:p-7 md:pb-7 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto w-full max-w-[1320px]">
            <WorkspacePageTitle
              workspace="Admin"
              title={active.label}
              description={active.description}
              crumb={`Finnish › ${active.label}`}
            />

            {tab === "overview" ? (
              <div className="space-y-4">
              <AutoTranslateCard isAdmin={!!isAdmin} onChanged={() => void refetch()} />
              <OverviewPanel
                data={data}
                isLoading={isLoading}
                pct={pct}
                pending={pending}
                running={running}
                log={log}
                lastRun={lastRun}
                onBackfill={runBackfill}
                onRefresh={() => void refetch()}
                onReview={() => go("review")}
                onJobs={() => go("jobs")}
              />
              </div>
            ) : tab === "review" ? (
              <Suspense fallback={<TabFallback />}>
                <FinnishReviewQueue />
              </Suspense>
            ) : tab === "browse" ? (
              <Suspense fallback={<TabFallback />}>
                <FinnishContentBrowser />
              </Suspense>
            ) : tab === "copy" ? (
              <Suspense fallback={<TabFallback />}>
                <SiteCopyTranslations />
              </Suspense>
            ) : (
              <JobsPanel
                runs={runs}
                fetching={runsFetching}
                onRefresh={() => void refetchRuns()}
              />
            )}
          </div>
        </main>
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
        style={{ borderColor: "#eceff3" }}
        aria-label="Translation sections"
      >
        <div className="grid grid-cols-5">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = t.key === active.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => go(t.key)}
                className="flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors active:scale-95"
                style={{ color: on ? BS.primary : "#6c757d" }}
                aria-current={on ? "page" : undefined}
              >
                <Icon className="size-5" strokeWidth={1.85} />
                <span className="truncate">{t.navLabel}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

/**
 * Master Finnish switch. On = every listing, place page and menu dish is
 * mirrored into Finnish automatically by the scheduled job, so nothing has to
 * be typed by hand. Off = Finnish only changes when an admin runs Translate.
 */
function AutoTranslateCard({ isAdmin, onChanged }: { isAdmin: boolean; onChanged: () => void }) {
  const getFn = useServerFn(getFinnishAutoTranslate);
  const setFn = useServerFn(setFinnishAutoTranslate);
  const [saving, setSaving] = useState(false);

  const { data, refetch } = useQuery({
    queryKey: ["admin", "fi-auto-translate"],
    queryFn: () => getFn({ data: undefined as never }),
    enabled: isAdmin,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const enabled = data?.enabled ?? true;

  async function toggle() {
    setSaving(true);
    try {
      const res = await setFn({ data: { enabled: !enabled } });
      toast.success(res.enabled ? "Finnish translation is on" : "Finnish translation is off");
      await refetch();
      onChanged();
    } catch (err) {
      toast.error((err as Error).message || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border bg-white p-4 sm:p-5" style={{ borderColor: "#eceff3" }}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Languages className="size-4" style={{ color: BS.primary }} />
            <span className="text-sm font-semibold text-[#212529]">Finnish translation</span>
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
              style={{
                background: enabled ? "#e8f5ee" : "#f1f3f5",
                color: enabled ? "#198754" : "#6c757d",
              }}
            >
              {enabled ? "On" : "Off"}
            </span>
          </div>
          <p className="mt-1 text-xs text-[#6c757d]">
            When on, every place page, listing and menu dish is kept in Finnish automatically — no manual typing.
            Hand-written Finnish is never overwritten.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Finnish translation"
          onClick={() => void toggle()}
          disabled={saving}
          className="relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50"
          style={{ background: enabled ? BS.primary : "#ced4da" }}
        >
          <span
            className="absolute top-0.5 size-6 rounded-full bg-white shadow transition-all"
            style={{ left: enabled ? "22px" : "2px" }}
          />
        </button>
      </div>
    </div>
  );
}

function OverviewPanel({
  data,
  isLoading,
  pct,
  pending,
  running,
  log,
  lastRun,
  onBackfill,
  onRefresh,
  onReview,
  onJobs,
}: {
  data:
    | {
        total: number;
        complete: number;
        stale: number;
        missing: number;
        pending: number;
        byEntity?: Array<{ entity: string; label: string; total: number; complete: number; pending: number }>;
      }
    | undefined;
  isLoading: boolean;
  pct: number;
  pending: number;
  running: boolean;
  log: string[];
  lastRun: { id: string; ok: boolean; started_at: string; source: string; processed: number; fields_translated: number; remaining: number; errors: string[] } | null;
  onBackfill: () => void;
  onRefresh: () => void;
  onReview: () => void;
  onJobs: () => void;
}) {
  if (isLoading && !data) {
    return <TabFallback />;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        <Kpi label="Complete" value={data ? `${pct}%` : "—"} hint={data ? `${data.complete} of ${data.total}` : "Loading"} tint="#198754" />
        <Kpi label="Missing" value={data?.missing ?? "—"} hint="No Finnish yet" tint="#dc3545" />
        <Kpi label="Stale" value={data?.stale ?? "—"} hint="English changed" tint="#f59e0b" />
        <Kpi label="Pending" value={pending || "0"} hint="Ready to translate" tint="#0d6efd" />
      </div>

      <div className="rounded-xl border bg-white p-4 sm:p-5" style={{ borderColor: "#eceff3" }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#212529]">Catalogue coverage</div>
            <p className="text-xs text-[#6c757d] mt-0.5">
              New listings translate automatically. Use Translate for anything still missing or stale.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onBackfill}
              disabled={running || pending === 0}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              style={{ background: BS.primary }}
            >
              {running ? <Loader2 className="size-4 animate-spin" /> : <Languages className="size-4" />}
              {running ? "Translating…" : `Translate ${pending}`}
            </button>
            <button
              type="button"
              onClick={onRefresh}
              disabled={running}
              className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-sm font-medium text-[#212529] disabled:opacity-50"
              style={{ borderColor: "#dee2e6" }}
            >
              <RefreshCw className="size-4" /> Refresh
            </button>
            <button
              type="button"
              onClick={onReview}
              className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-sm font-medium text-[#212529]"
              style={{ borderColor: "#dee2e6" }}
            >
              <ClipboardCheck className="size-4" /> Review
            </button>
          </div>
        </div>

        <div className="mt-4 h-2 w-full overflow-hidden rounded-full" style={{ background: "#e9ecef" }}>
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, background: pct >= 90 ? "#198754" : BS.primary }}
          />
        </div>

        {data?.byEntity && data.byEntity.length > 0 ? (
          <div className="mt-4 overflow-hidden rounded-lg border" style={{ borderColor: "#eceff3" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "#f8f9fb" }}>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[#6c757d]">
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 text-right font-semibold">Total</th>
                  <th className="px-3 py-2 text-right font-semibold">Done</th>
                  <th className="px-3 py-2 text-right font-semibold">Pending</th>
                  <th className="hidden sm:table-cell px-3 py-2 font-semibold w-40">Progress</th>
                </tr>
              </thead>
              <tbody>
                {data.byEntity.map((e) => {
                  const donePct = e.total > 0 ? Math.round((e.complete / e.total) * 100) : 0;
                  return (
                    <tr key={e.entity} className="border-t" style={{ borderColor: "#eceff3" }}>
                      <td className="px-3 py-2 font-medium text-[#212529]">{e.label}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#495057]">{e.total}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#198754]">{e.complete}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${e.pending > 0 ? "text-amber-600 font-semibold" : "text-[#adb5bd]"}`}>
                        {e.pending}
                      </td>
                      <td className="hidden sm:table-cell px-3 py-2">
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#e9ecef" }}>
                          <div className="h-full rounded-full" style={{ width: `${donePct}%`, background: donePct === 100 ? "#198754" : BS.primary }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {lastRun && (
          <button
            type="button"
            onClick={onJobs}
            className="mt-4 w-full flex items-center justify-between gap-3 rounded-lg border bg-[#f8f9fb] px-3 py-2.5 text-left hover:bg-[#eef1f5]"
            style={{ borderColor: "#eceff3" }}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className={`size-2 rounded-full shrink-0 ${lastRun.ok ? "bg-emerald-500" : "bg-rose-500"}`} />
              <span className="text-xs text-[#6c757d] truncate">
                Last {lastRun.source === "cron" ? "scheduled" : "manual"} run · {new Date(lastRun.started_at).toLocaleString()}
              </span>
            </span>
            <span className="text-xs font-medium text-[#495057] shrink-0">
              {lastRun.processed} listings · {lastRun.remaining} left
            </span>
          </button>
        )}
      </div>

      {(running || log.length > 0) && (
        <div className="rounded-xl border bg-white p-4" style={{ borderColor: "#eceff3" }}>
          <div className="text-sm font-semibold text-[#212529] mb-2">Live log</div>
          <ul className="max-h-40 overflow-y-auto space-y-1 text-xs text-[#6c757d] font-mono">
            {log.length === 0 ? <li>Starting…</li> : log.slice(0, 20).map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function JobsPanel({
  runs,
  fetching,
  onRefresh,
}: {
  runs: Array<{
    id: string;
    ok: boolean;
    started_at: string;
    source: string;
    processed: number;
    fields_translated: number;
    remaining: number;
    errors: string[];
  }> | undefined;
  fetching: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: "#eceff3" }}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b" style={{ borderColor: "#eceff3" }}>
        <p className="text-xs text-[#6c757d]">
          Runs every 30 minutes and translates listings that are new or whose English text changed.
        </p>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-2.5 py-1.5 text-xs font-medium text-[#212529]"
          style={{ borderColor: "#dee2e6" }}
        >
          <RefreshCw className={`size-3.5 ${fetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {runs && runs.length > 0 ? (
        <ul className="divide-y" style={{ borderColor: "#eceff3" }}>
          {runs.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
              <span className="flex items-center gap-2 min-w-0">
                <span className={`size-2 rounded-full shrink-0 ${r.ok ? "bg-emerald-500" : "bg-rose-500"}`} />
                <span className="text-sm text-[#212529]">{new Date(r.started_at).toLocaleString()}</span>
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  style={{ background: r.source === "cron" ? "#eef1f5" : "#e7f1ff", color: r.source === "cron" ? "#6c757d" : BS.primary }}
                >
                  {r.source === "cron" ? "Scheduled" : "Manual"}
                </span>
              </span>
              <span className="text-xs text-[#6c757d]">
                {r.processed} listing{r.processed === 1 ? "" : "s"} · {r.fields_translated} fields · {r.remaining} left
                {r.errors.length > 0 ? ` · ${r.errors[0]}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-4 py-10 text-center text-sm text-[#6c757d]">No runs yet — the next scheduled job will appear here.</p>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  tint,
}: {
  label: string;
  value: ReactNode;
  hint: string;
  tint: string;
}) {
  return (
    <div className="rounded-xl border bg-white px-3 py-3 sm:px-4 sm:py-3.5" style={{ borderColor: "#eceff3" }}>
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full shrink-0" style={{ background: tint }} />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#6c757d]">{label}</span>
      </div>
      <div className="mt-1.5 text-2xl font-bold tabular-nums text-[#212529] leading-none">{value}</div>
      <div className="mt-1 text-[11px] text-[#6c757d]">{hint}</div>
    </div>
  );
}
